import { useEffect } from 'react';
import { Outlet, redirect, useLoaderData, useLocation, useNavigation } from 'react-router';
import type { LoaderFunctionArgs } from 'react-router';
import { api } from '../lib/api';
import { showLegacyHost } from '../lib/workspaceHost';
import type { AuthState } from '../lib/types';
import { ModuleLoader } from '../components/loaders/Loaders';
import { loaderTargetForPath } from '../components/loaders/loaderModules';
import BillingGate from '../features/pricing/BillingGate';
import { guestPathAllowed } from '../features/guild/GuestBanner';

export interface RootLoaderData {
  auth: AuthState;
}

const PLAN_FREE_PATHS = ['/pricing', '/settings'];

export async function rootLoader({ request }: LoaderFunctionArgs): Promise<RootLoaderData | Response> {
  const url = new URL(request.url);
  if (url.searchParams.has('invite')) return redirect(`/invite?invite=${encodeURIComponent(url.searchParams.get('invite')!)}`);

  const auth = await readAuthState();
  if (!auth.authenticated) {
    const next = `${url.pathname}${url.search}`;
    if (auth.needsSetup) return redirect('/signup');
    return redirect(`/signin${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`);
  }
  if ((auth.user?.studios?.length || 0) > 1 && !auth.studioChosen) {
    const next = `${url.pathname}${url.search}`;
    return redirect(`/studios${next && next !== '/' ? `?next=${encodeURIComponent(next)}` : ''}`);
  }
  if (auth.user?.guest && !guestPathAllowed(url.pathname, auth.user.guest.projectIds)) return redirect('/guild?tab=contracts');
  if (!auth.user?.guest && auth.planStatus === 'pending' && !PLAN_FREE_PATHS.includes(url.pathname)) return redirect('/pricing?start=1');
  return { auth };
}

async function readAuthState(): Promise<AuthState> {
  let lastError: unknown;
  for (let attempt = 0; attempt < 8; attempt += 1) {
    try {
      return await api.authState();
    } catch (error) {
      lastError = error;
      await new Promise(resolve => setTimeout(resolve, 400));
    }
  }
  throw lastError instanceof Error ? lastError : new Error('The API did not respond.');
}

export default function RootLayout() {
  const { auth } = useLoaderData() as RootLoaderData;
  const navigation = useNavigation();
  const location = useLocation();
  const destination = navigation.state === 'loading' ? loaderTargetForPath(navigation.location.pathname) : null;
  const current = loaderTargetForPath(location.pathname);
  const transition =
    destination?.kind === 'module' && (current.kind !== 'module' || current.module !== destination.module) ? destination.module : null;

  useEffect(() => {
    return () => showLegacyHost(false);
  }, []);

  return (
    <>
      {navigation.state === 'loading' ? <span className="route-progress" aria-hidden="true" /> : null}
      {transition ? <ModuleLoader key={transition} module={transition} layout="toast" /> : null}
      <Outlet />
      <BillingGate canPurchase={auth.user?.role === 'owner'} />
    </>
  );
}
